import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { FitHighlight, Tab, TabContent, TabDivider, TabHighlight, TabList } from './Tabs.styles'

// Layout effect in the browser (no flash of the unplaced highlight), plain effect on the server.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

interface CardTab<T extends string> {
  id: T
  label: ReactNode
  /** DOM id of the tab button, e.g. a deep-link anchor. */
  domId?: string
}

interface CardTabsProps<T extends string> {
  tabs: CardTab<T>[]
  activeId: T
  /** NoInfer: `T` comes from the tabs, so a `useState` setter can be passed directly. */
  onSelect: (id: NoInfer<T>) => void
  /** Id of the `TabPanel` the tabs control. */
  panelId: string
  label?: string
  /** Tabs hug their labels, left-aligned, instead of sharing the row equally. */
  fit?: boolean
  /** Test-automation hook: each tab gets `data-cy="<dataCy>-<tab id>"`. */
  dataCy?: string
}

/** Measures the active `fit` tab; null until measured (and on the server). */
const useActiveTabBox = (fit: boolean, activeId: string) => {
  const listRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ left: number; width: number } | null>(null)

  useIsomorphicLayoutEffect(() => {
    if (!fit) return undefined
    const tab = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!tab) return undefined
    const measure = () => setBox({ left: tab.offsetLeft, width: tab.offsetWidth })
    measure()
    // Labels can change width once the web font loads.
    const observer = new ResizeObserver(measure)
    observer.observe(tab)
    return () => observer.disconnect()
  }, [fit, activeId])

  // A narrow screen scrolls the row: bring the active tab into it.
  useEffect(() => {
    const list = listRef.current
    const tab = list?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!fit || !list || !tab || list.scrollWidth <= list.clientWidth) return
    const right = tab.offsetLeft + tab.offsetWidth
    if (tab.offsetLeft < list.scrollLeft) list.scrollTo({ left: tab.offsetLeft, behavior: 'smooth' })
    else if (right > list.scrollLeft + list.clientWidth)
      list.scrollTo({ left: right - list.clientWidth, behavior: 'smooth' })
  }, [fit, activeId])

  return { listRef, box }
}

/**
 * The card tab bar (Figma "Tab List - Main Actions"), with the white surface sliding to the
 * active tab. Equal-width tabs place it from the index, so the server render is exact; `fit`
 * tabs measure it, the active tab painting its own surface until then.
 */
export const CardTabs = <T extends string>({
  tabs,
  activeId,
  onSelect,
  panelId,
  label,
  fit = false,
  dataCy,
}: CardTabsProps<T>) => {
  const { listRef, box } = useActiveTabBox(fit, activeId)
  // Below desktop fit tabs share the row; a lone tab keeps its label width.
  const stretch = fit && tabs.length > 1
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === activeId),
  )

  return (
    <TabList ref={listRef} role="tablist" aria-label={label} $fit={fit}>
      {fit ? (
        box && (
          <FitHighlight
            aria-hidden="true"
            style={{ '--tab-left': `${box.left}px`, '--tab-width': `${box.width}px` } as CSSProperties}
          />
        )
      ) : (
        // Dividers are 1px wide, so the highlight steps by the tab width plus 1px.
        <TabHighlight $index={activeIndex} $count={tabs.length} $gap={1} aria-hidden="true" />
      )}
      {tabs.map((tab, index) => {
        const active = tab.id === activeId
        return (
          <Fragment key={tab.id}>
            {!fit && index > 0 && <TabDivider />}
            <Tab
              id={tab.domId}
              type="button"
              role="tab"
              aria-controls={panelId}
              aria-selected={active}
              $active={active}
              $fit={fit}
              $stretch={stretch}
              $filled={active && !box}
              data-cy={dataCy && `${dataCy}-${tab.id}`}
              onClick={() => onSelect(tab.id)}>
              {tab.label}
            </Tab>
            {/* Figma closes every fit tab with a divider, the last one included, except where
                stretched tabs end on the card's edge. */}
            {fit && <TabDivider $edge={stretch && index === tabs.length - 1} />}
          </Fragment>
        )
      })}
    </TabList>
  )
}

type TabPanelProps = Omit<ComponentProps<typeof TabContent>, '$animate'> & {
  /** The active tab: content fades in each time it changes, not on first render. */
  activeKey: string
  /** Fade in on first render too: content that only mounts on a click, never on page load. */
  appear?: boolean
}

export const TabPanel = ({ activeKey, appear = false, children, ...props }: TabPanelProps) => {
  const [shownKey, setShownKey] = useState(activeKey)
  const [animate, setAnimate] = useState(appear)
  if (activeKey !== shownKey) {
    setShownKey(activeKey)
    setAnimate(true)
  }

  return (
    <TabContent key={activeKey} role="tabpanel" $animate={animate} {...props}>
      {children}
    </TabContent>
  )
}
